import React from 'react';
import styled from 'styled-components';

import { orbitSpace, orbitTokens, orbitType } from '../styles';

export type OrbitAnalysisVariant = 'bars' | 'donut';

export interface IOrbitAnalysisCardProps {
  variant?: OrbitAnalysisVariant;
  testId?: string;
  /**
   * Head copy: the governed-analysis tag or a non-answered outcome label. Omitted, the card has no
   * head row and opens on its prose at the card's own padding.
   *
   * TODO(AF-20): `4952:8091` suffixes it with a latency, "| 0.8s". NOT built: no field on the turn
   * carries elapsed time - `OrbitTurn` has `created_at` and nothing else temporal, and the contract
   * adds none. A client-side timer would measure our own 5s poll rather than the backend's work, so
   * it would be a different number wearing the same label. It needs a duration from PLA-8839.
   */
  label?: string;
  labelTestId?: string;
  labelIcon?: React.ReactNode;
  /** Inks the head in the danger colour, for the error and timeout outcomes. */
  negative?: boolean;
  /** The prose body (`div.analysis-text`), whose top padding is variant-specific. */
  text?: React.ReactNode;
  textTestId?: string;
  children?: React.ReactNode;
}

/**
 * figma-spec-v2.md §8 draws two analysis cards. `4952:8087` (the bars answer) pads 16/18/24/18 and
 * draws no head, as no current frame does; `4952:8536` (the donut and chips answer) pads
 * 28/18/16/18 and heads with the smaller uppercase `📈 Governed Analysis` tag. §8 outranks §7c's
 * "r0" prose on the badge radius (decision D3).
 *
 * The STROKE is not one of the differences. `4952:8087` is the only node anywhere that reads no
 * stroke on this card; v2's own §5 L2 row says "1px #268AFF all round", and all three analysis-card
 * nodes in figma-spec-v3.md — `5032:10873`, `5032:11580`, `5017:9864` — read `#268AFF / 1`. The
 * single transparent reading was superseded, and it left a data answer with no visible edge at all
 * (user: "border issue", 2026-09-11), so both variants take the stroke.
 */
const VARIANTS = {
  bars: {
    padding: '16px 18px 24px',
    borderColor: orbitTokens.action,
    headPadding: '12px',
    headBorder: 'transparent',
    textPadding: '6px',
    labelSize: '14px',
    labelSpacing: '0',
    labelHeight: '19.6px',
    labelCase: 'none'
  },
  donut: {
    padding: '28px 18px 16px',
    borderColor: orbitTokens.action,
    headPadding: '10px',
    headBorder: orbitTokens.border,
    textPadding: '2px',
    labelSize: '11px',
    labelSpacing: '0',
    labelHeight: '15.4px',
    labelCase: 'uppercase'
  }
} as const;

// Both variants carry the same 1px stroke, so switching variant cannot shift the 447 content column.
const Card = styled.section<{ $variant: OrbitAnalysisVariant }>`
  /* PERX3-REDESIGN round 6: one 16px inset on all four sides under the flag, in place of the two
     variant-specific Figma paddings the fallback keeps. */
  padding: ${({ $variant }) => orbitSpace('space-card', VARIANTS[$variant].padding)};
  border: 1px solid ${({ $variant }) => VARIANTS[$variant].borderColor};
  border-radius: 14px;
  background: ${orbitTokens.surface};

  /*
    Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5).

    Doubled class, so the card's rhythm outranks a child that declares its own margin 0. At single
    specificity this rule lost to Text and won against Rows purely on stylesheet order, which put the
    head, the prose and the detail list flush against each other at 0 while every later block sat at
    8 - the uneven vertical rhythm the user reported (2026-09-11). 8 is div.analysis-card's drawn gap
    (5017:9864).
  */
  && > * + * {
    margin-top: ${orbitSpace('space-section', '8px')};
  }
`;

// The donut head rules off at 1px, not the panel header's 0.8: div.analysis-tag 5017:9865 reads
// Border T R B L = 0 0 1 0. The bars head 4952:8088 draws none, so it takes a transparent rule and
// the two variants keep the same box.
const Head = styled.div<{ $variant: OrbitAnalysisVariant }>`
  display: flex;
  align-items: center;
  padding-bottom: ${({ $variant }) => VARIANTS[$variant].headPadding};
  border-bottom: 1px solid ${({ $variant }) => VARIANTS[$variant].headBorder};
`;

const Label = styled.h4<{ $variant: OrbitAnalysisVariant; $negative: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 0;
  color: ${({ $negative }) => ($negative ? orbitTokens.negative : orbitTokens.action)};
  font-size: ${({ $variant }) => VARIANTS[$variant].labelSize};
  font-weight: 700;
  letter-spacing: ${({ $variant }) => VARIANTS[$variant].labelSpacing};
  line-height: ${({ $variant }) => VARIANTS[$variant].labelHeight};
  text-transform: ${({ $variant }) => VARIANTS[$variant].labelCase};

  > * + * {
    margin-left: 6px;
  }
`;

const Text = styled.p<{ $variant: OrbitAnalysisVariant; $headless: boolean }>`
  margin: 0;
  padding-top: ${({ $variant, $headless }) => ($headless ? '0' : VARIANTS[$variant].textPadding)};
  color: ${orbitTokens.textSecondary};
  ${orbitType.conversation}
  font-weight: 400;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const OrbitAnalysisCard: React.FC<IOrbitAnalysisCardProps> = ({
  variant = 'bars',
  testId = 'orbit-analysis-card',
  label,
  labelTestId,
  labelIcon,
  negative = false,
  text,
  textTestId,
  children
}) => (
  <Card $variant={variant} data-testid={testId} data-variant={variant}>
    {label === undefined ? null : (
      <Head $variant={variant}>
        <Label $variant={variant} $negative={negative} data-testid={labelTestId}>
          {labelIcon}
          <span>{label}</span>
        </Label>
      </Head>
    )}
    {text !== undefined && text !== '' ? (
      <Text $variant={variant} $headless={label === undefined} data-testid={textTestId}>
        {text}
      </Text>
    ) : null}
    {children}
  </Card>
);

export default OrbitAnalysisCard;
